import React, { useEffect, useRef, useState } from "react";
import RichTextEditor from "../components/RichTextEditor";
import ImageLightbox from "../components/ImageLightbox";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { ITicket, ITicketAssignment, ITicketMessage, ITicketTaskItem } from "../ticket-module/models/ITicket";
import { getTicketById, addMessage, getUserListByRole, reassignTicket, getAllStatuses, updateTicketStatus, deleteTicket, assignUsersToTicket, getDepartmentUsers, getTicketChildren, setTicketTaskCompletion, reorderTicketTasks, updateTicketTask, addTicketTask } from "../ticket-module/services/ticketService";
import TicketDocumentUpload from "../components/TicketDocumentUpload";
import { getUserToken } from "../utils/Util";
import { checkCanSetMessageColor } from "../services/checkRole";
import EditTicketModal from "../ticket-module/components/EditTicketModal";
import LinkTicketModal from "../ticket-module/components/LinkTicketModal";
import ChildTicketPreviewModal from "../ticket-module/components/ChildTicketPreviewModal";

const CLOSED_STATUS_ID = "c11e154e-6839-40be-9c9d-8209693034f4";
const DEFAULT_TASK_WEIGHT = 100;

const MESSAGE_COLOR_PALETTE = [
  { label: "Sarı",     value: "#FDE68A" },
  { label: "Turuncu",  value: "#FED7AA" },
  { label: "Kırmızı",  value: "#FECACA" },
  { label: "Mor",      value: "#DDD6FE" },
  { label: "Mavi",     value: "#BFDBFE" },
  { label: "Yeşil",    value: "#BBF7D0" },
  { label: "Turkuaz",  value: "#99F6E4" },
  { label: "Gri",      value: "#E5E7EB" },
];

const PRIORITY: Record<number, { label: string; color: string; bg: string }> = {
  1: { label: "Düşük",   color: "#16a34a", bg: "#f0fdf4" },
  2: { label: "Orta",    color: "#2563eb", bg: "#eff6ff" },
  3: { label: "Yüksek",  color: "#ea580c", bg: "#fff7ed" },
  4: { label: "Kritik",  color: "#dc2626", bg: "#fef2f2" },
};

const STATUS: Record<string, { color: string; bg: string; border: string }> = {
  "Açık":             { color: "#16a34a", bg: "#f0fdf4", border: "#bbf7d0" },
  "Ticket yaratıldı": { color: "#2563eb", bg: "#eff6ff", border: "#bfdbfe" },
  "Kapalı":           { color: "#6b7280", bg: "#f9fafb", border: "#e5e7eb" },
  "Beklemede":        { color: "#d97706", bg: "#fffbeb", border: "#fde68a" },
};

function getStatus(desc: string) {
  return STATUS[desc] ?? { color: "#6b7280", bg: "#f9fafb", border: "#e5e7eb" };
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleString("tr-TR", {
    day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}

function initials(name: string): string {
  return name.split(" ").map((w) => w[0]).join("").toUpperCase().slice(0, 2);
}

function parseTodoMessage(text: string): { taskId: string; userId: string; completed: boolean; title: string } | null {
  const match = text.match(/^@todo\s+(\S+)\s+(\S+)\s+(tamamlandı|geri alındı):\s*(.*)$/);
  if (!match) return null;
  return { taskId: match[1], userId: match[2], completed: match[3] === "tamamlandı", title: match[4] };
}

const AVATAR_COLORS = ["#6366f1","#2563eb","#16a34a","#dc2626","#d97706","#0891b2","#7c3aed"];
function avatarColor(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash += name.charCodeAt(i);
  return AVATAR_COLORS[hash % AVATAR_COLORS.length];
}

export default function TicketDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  // Yeni sekmede / doğrudan linkle açıldıysa bu tab'ın geçmişinde dönecek sayfa yoktur
  // (history.length === 1) — bu durumda Destek sayfasına git, yoksa normal geri git.
  const goBack = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate("/destek");
  };

  const [ticket, setTicket]   = useState<ITicket | null>(null);
  const [messages, setMessages] = useState<ITicketMessage[]>([]);
  const [loadingTicket, setLoadingTicket] = useState(true);
  const [newMsg, setNewMsg]   = useState("");
  const [msgImage, setMsgImage] = useState<File | null>(null);
  const [msgBgColor, setMsgBgColor] = useState("");
  const [canSetMessageColor, setCanSetMessageColor] = useState(false);
  const [lightboxSrc, setLightboxSrc] = useState<string | null>(null);
  const [showExpanded, setShowExpanded] = useState(false);
  const [expandedZoom, setExpandedZoom] = useState<number>(() => {
    const saved = Number(localStorage.getItem("ticketExpandedZoom"));
    return saved >= 0.7 && saved <= 1.8 ? saved : 1;
  });
  const changeZoom = (next: number) => {
    const z = Math.min(1.8, Math.max(0.7, Math.round(next * 10) / 10));
    setExpandedZoom(z);
    localStorage.setItem("ticketExpandedZoom", String(z));
  };
  const expandedEndRef = useRef<HTMLDivElement>(null);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState("");
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Reassign
  const [userList, setUserList]             = useState<{ id: string; name: string }[]>([]);
  const [selectedUserId, setSelectedUserId] = useState("");
  const [reassigning, setReassigning]       = useState(false);
  const [reassignMsg, setReassignMsg]       = useState<{ type: "success" | "error"; text: string } | null>(null);

  // Status update
  const [statusList, setStatusList]           = useState<{ id: string; description: string }[]>([]);
  const [selectedStatusId, setSelectedStatusId] = useState("");
  const [updatingStatus, setUpdatingStatus]   = useState(false);
  const [statusMsg, setStatusMsg]             = useState<{ type: "success" | "error"; text: string } | null>(null);

  // Edit modal
  const [showEdit, setShowEdit] = useState(false);

  // Link ticket modal
  const [showLinkModal, setShowLinkModal] = useState(false);

  // Bağlı ticketlar (children + parent)
  const [children, setChildren] = useState<ITicket[]>([]);
  const [previewTicketId, setPreviewTicketId] = useState<string | null>(null);

  // Combined submit loading
  const [submitting, setSubmitting] = useState(false);

  // Self-take ticket
  const [takingTicket, setTakingTicket] = useState(false);
  const [takeError, setTakeError]       = useState("");

  // Close ticket
  const [showClosePanel, setShowClosePanel] = useState(false);
  const [showDocUpload, setShowDocUpload] = useState(false);
  const [docCount, setDocCount] = useState(0);

  // Görevler (tasks)
  const [showTasksPopup, setShowTasksPopup] = useState(false);
  const [taskBusyId, setTaskBusyId] = useState<string | null>(null);
  const [taskError, setTaskError] = useState("");
  const [editingTaskId, setEditingTaskId] = useState<string | null>(null);
  const [editTaskTitle, setEditTaskTitle] = useState("");
  const [newTaskTitle, setNewTaskTitle] = useState("");
  const [newTaskWeight, setNewTaskWeight] = useState(String(DEFAULT_TASK_WEIGHT));
  const [addingTask, setAddingTask] = useState(false);
  const [closeMsg, setCloseMsg]             = useState("");
  const [closing, setClosing]               = useState(false);
  const [closeError, setCloseError]         = useState("");

  // Watchers (izleyiciler) popup
  const [showWatchersPopup, setShowWatchersPopup]   = useState(false);
  const [selectedWatchers, setSelectedWatchers]     = useState<string[]>([]);
  const [addingWatchers, setAddingWatchers]         = useState(false);
  const [watchersError, setWatchersError]           = useState("");

  // Delete
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting]           = useState(false);
  const [deleteError, setDeleteError]     = useState("");

  // Dept membership
  const [isInTicketDept, setIsInTicketDept] = useState(false);

  // Page-level error
  const [pageError, setPageError] = useState<string | null>(null);

  // Responsive
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 1024);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);

  // Current user id to detect own messages
  const currentUserId = getUserToken()?.userId ?? "";

  function extractApiError(err: any): string {
    const data = err?.response?.data;
    return Array.isArray(data) && data[0]?.description
      ? data[0].description
      : err?.message ?? "Bir hata oluştu.";
  }

  const reloadTicket = async () => {
    if (!id) return;
    const res = await getTicketById(id);
    setTicket(res.data);
    setMessages(res.data.messages ?? []);
  };

  const handleToggleTask = async (task: ITicketTaskItem) => {
    setTaskBusyId(task.id);
    setTaskError("");
    try {
      await setTicketTaskCompletion({ taskId: task.id, isCompleted: !task.isCompleted });
      await reloadTicket();
    } catch {
      setTaskError("Görev güncellenemedi. Lütfen tekrar deneyin.");
    } finally {
      setTaskBusyId(null);
    }
  };

  const handleMoveTask = async (sortedTasks: ITicketTaskItem[], index: number, direction: -1 | 1) => {
    if (!id) return;
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= sortedTasks.length) return;
    const reordered = [...sortedTasks];
    const [moved] = reordered.splice(index, 1);
    reordered.splice(targetIndex, 0, moved);
    setTaskBusyId(sortedTasks[index].id);
    setTaskError("");
    try {
      await reorderTicketTasks({ ticketId: id, taskIds: reordered.map((t) => t.id) });
      await reloadTicket();
    } catch {
      setTaskError("Görev sırası güncellenemedi. Lütfen tekrar deneyin.");
    } finally {
      setTaskBusyId(null);
    }
  };

  const handleStartEditTask = (task: ITicketTaskItem) => {
    setEditingTaskId(task.id);
    setEditTaskTitle(task.title);
    setTaskError("");
  };

  const handleCancelEditTask = () => {
    setEditingTaskId(null);
    setEditTaskTitle("");
  };

  const handleSaveEditTask = async (task: ITicketTaskItem) => {
    const trimmed = editTaskTitle.trim();
    if (!trimmed) return;
    if (trimmed === task.title) { handleCancelEditTask(); return; }
    setTaskBusyId(task.id);
    setTaskError("");
    try {
      await updateTicketTask({ taskId: task.id, title: trimmed, weight: task.weight });
      await reloadTicket();
      handleCancelEditTask();
    } catch {
      setTaskError("Görev adı güncellenemedi. Lütfen tekrar deneyin.");
    } finally {
      setTaskBusyId(null);
    }
  };

  const handleAddTask = async () => {
    if (!id) return;
    const trimmed = newTaskTitle.trim();
    if (!trimmed) return;
    const parsedWeight = parseInt(newTaskWeight, 10);
    const weight = Number.isFinite(parsedWeight) && parsedWeight > 0 ? parsedWeight : DEFAULT_TASK_WEIGHT;
    setAddingTask(true);
    setTaskError("");
    try {
      await addTicketTask({ title: trimmed, weight, ticketId: id });
      await reloadTicket();
      setNewTaskTitle("");
      setNewTaskWeight(String(DEFAULT_TASK_WEIGHT));
    } catch {
      setTaskError("Görev eklenemedi. Lütfen tekrar deneyin.");
    } finally {
      setAddingTask(false);
    }
  };

  useEffect(() => {
    if (!id) return;
    const YAZILIM_DEPT_ID = "a1adf997-e85a-40da-823c-7477e0d2cf09";
    const DESTEK_DEPT_ID  = "c631c065-2de5-4327-acfa-57767f390820";

    getTicketById(id)
      .then((res) => {
        const t = res.data;
        setTicket(t);
        setMessages(t.messages ?? []);
        // Ticket'in departmanına üye miyim?
        const deptId = t.ticketDepartmentId;
        if (deptId === YAZILIM_DEPT_ID || deptId === DESTEK_DEPT_ID) {
          getDepartmentUsers(deptId)
            .then((r) => setIsInTicketDept((r.data ?? []).includes(currentUserId)))
            .catch(() => {});
        }
      })
      .catch((err) => setPageError(extractApiError(err)))
      .finally(() => setLoadingTicket(false));

    // Get assignable users
    getUserListByRole()
      .then((res) => {
        const list = (res.data ?? []).map((u: any) => ({
          id: u.id,
          name: `${u.customerName} ${u.customerSurName}`,
        }));
        setUserList(list);
      })
      .catch(() => {});

    // Get all statuses
    getAllStatuses()
      .then((res) => setStatusList(res.data ?? []))
      .catch(() => {});

    // Get linked (child) tickets
    getTicketChildren(id)
      .then((res) => setChildren(res.data ?? []))
      .catch(() => setChildren([]));

    checkCanSetMessageColor()
      .then(setCanSetMessageColor)
      .catch(() => setCanSetMessageColor(false));
  }, [id]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Büyük görünüm açıkken: en son mesaja in, sayfa kaydırmasını kilitle, Esc ile kapat.
  useEffect(() => {
    if (!showExpanded) return;
    expandedEndRef.current?.scrollIntoView();
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !lightboxSrc) setShowExpanded(false);
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onKey);
    };
  }, [showExpanded, lightboxSrc]);

  useEffect(() => {
    if (showExpanded) expandedEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, showExpanded]);

  const handleSend = async () => {
    if (!newMsg.trim() || !id) return;
    setSending(true);
    setSubmitting(true);
    setSendError("");
    try {
      // 1. Mesajı gönder (contentEditable HTML olarak gelir, direkt gönder)
      await addMessage(id, newMsg.trim(), msgImage ?? undefined, msgBgColor || undefined);

      // 2. Durum güncelle (seçildiyse)
      if (selectedStatusId) {
        await updateTicketStatus(id, selectedStatusId);
        const statusDesc = statusList.find((s) => s.id === selectedStatusId)?.description ?? selectedStatusId;
        const currentUserName = userList.find((u) => u.id === currentUserId)?.name ?? "Bir yetkili";
        await addMessage(id, `@TicketBilgi ${currentUserName} bu ticketin durumunu "${statusDesc}" olarak güncelledi`);
      }

      // 3. Kişiye ata (seçildiyse)
      if (selectedUserId) {
        await reassignTicket(id, selectedUserId);
        const assignedUserName = userList.find((u) => u.id === selectedUserId)?.name ?? selectedUserId;
        const currentUserName = userList.find((u) => u.id === currentUserId)?.name ?? "Bir yetkili";
        await addMessage(id, `@TicketBilgi ${currentUserName} bu ticketi "${assignedUserName}" kişisine atadı`);
      }

      await reloadTicket();
      setNewMsg("");
      setMsgImage(null);
      setMsgBgColor("");
      setSelectedStatusId("");
      setSelectedUserId("");
    } catch {
      setSendError("İşlem tamamlanamadı. Lütfen tekrar deneyin.");
    } finally {
      setSending(false);
      setSubmitting(false);
    }
  };

  const handleStatusUpdate = async () => {
    if (!selectedStatusId || !id) return;
    setUpdatingStatus(true);
    setStatusMsg(null);
    try {
      await updateTicketStatus(id, selectedStatusId);
      const statusDesc = statusList.find((s) => s.id === selectedStatusId)?.description ?? selectedStatusId;
      const currentUserName = userList.find((u) => u.id === currentUserId)?.name ?? "Bir yetkili";
      await addMessage(id, `@TicketBilgi ${currentUserName} bu ticketin durumunu "${statusDesc}" olarak güncelledi`);
      setStatusMsg({ type: "success", text: "Durum başarıyla güncellendi. Sayfa yenileniyor..." });
      setTimeout(() => window.location.reload(), 1500);
    } catch {
      setStatusMsg({ type: "error", text: "Durum güncellenemedi. Lütfen tekrar deneyin." });
      setUpdatingStatus(false);
    }
  };

  const doReassign = async (userId: string) => {
    if (!id) return;
    setReassigning(true);
    setReassignMsg(null);
    try {
      await reassignTicket(id, userId);
      const assignedUserName = userList.find((u) => u.id === userId)?.name ?? userId;
      const currentUserName = userList.find((u) => u.id === currentUserId)?.name ?? "Bir yetkili";
      await addMessage(id, `@TicketBilgi ${currentUserName} bu ticketi "${assignedUserName}" kişisine atadı`);
      const alreadyWatcher = ticket?.assignments?.some(a => a.assignedUserId === userId) ?? false;
      if (!alreadyWatcher) {
        await assignUsersToTicket(id, [userId]);
        await addMessage(id, `@TicketBilgi ${currentUserName} "${assignedUserName}" kişisini izleyici olarak ekledi`);
      }
      setReassignMsg({ type: "success", text: "Ticket başarıyla atandı. Sayfa yenileniyor..." });
      setTimeout(() => window.location.reload(), 1500);
    } catch {
      setReassignMsg({ type: "error", text: "Atama başarısız oldu. Lütfen tekrar deneyin." });
      setReassigning(false);
    }
  };

  const handleReassign = () => doReassign(selectedUserId);

  const TAKE_STATUS_ID = "c94f0b39-47cf-43ea-9c06-a886943c1030";
  const handleSelfTake = async () => {
    if (!id || !currentUserId) return;
    setTakingTicket(true);
    setTakeError("");
    try {
      await reassignTicket(id, currentUserId);
      await updateTicketStatus(id, TAKE_STATUS_ID);
      const myName = userList.find(u => u.id === currentUserId)?.name ?? "Bir yetkili";
      await addMessage(id, `@TicketBilgi ${myName} bu ticketi üzerine aldı`);
      window.location.reload();
    } catch {
      setTakeError("Ticket alınamadı. Lütfen tekrar deneyin.");
      setTakingTicket(false);
    }
  };

  const autoTakeTriggered = useRef(false);
  useEffect(() => {
    if (location.state?.autoTake && !autoTakeTriggered.current && userList.length > 0 && currentUserId) {
      autoTakeTriggered.current = true;
      doReassign(currentUserId);
    }
  }, [userList]);

  const handleDelete = async () => {
    if (!id) return;
    setDeleting(true);
    setDeleteError("");
    try {
      await deleteTicket(id);
      navigate("/destek");
    } catch {
      setDeleteError("Ticket silinemedi. Lütfen tekrar deneyin.");
      setDeleting(false);
    }
  };

  const handleCloseTicket = async () => {
    if (!closeMsg.trim() || !id) return;
    setClosing(true);
    setCloseError("");
    try {
      await addMessage(id, closeMsg.trim());
      await updateTicketStatus(id, CLOSED_STATUS_ID);
      window.location.reload();
    } catch {
      setCloseError("Ticket kapatılamadı. Lütfen tekrar deneyin.");
      setClosing(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && e.ctrlKey) {
      e.preventDefault();
      handleSend();
    }
  };

  if (loadingTicket) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "60vh" }}>
        <div className="spinner-border text-primary" role="status" />
      </div>
    );
  }

  if (pageError) {
    return (
      <div style={{ maxWidth: "520px", margin: "80px auto", padding: "0 16px" }}>
        <div style={{
          background: "#fff", borderRadius: "16px",
          border: "1.5px solid #fecaca",
          boxShadow: "0 4px 24px rgba(220,38,38,0.10)",
          overflow: "hidden",
        }}>
          <div style={{ height: "5px", background: "linear-gradient(90deg, #dc2626, #ef4444)" }} />
          <div style={{ padding: "32px", textAlign: "center" }}>
            <div style={{
              width: "56px", height: "56px", borderRadius: "50%",
              background: "#fef2f2", display: "inline-flex",
              alignItems: "center", justifyContent: "center", marginBottom: "16px",
            }}>
              <i className="bi bi-shield-exclamation" style={{ fontSize: "1.6rem", color: "#dc2626" }}></i>
            </div>
            <h6 style={{ fontWeight: 800, color: "#111827", marginBottom: "8px" }}>Erişim Hatası</h6>
            <p style={{ color: "#6b7280", fontSize: "0.9rem", margin: "0 0 24px", lineHeight: 1.6 }}>
              {pageError}
            </p>
            <button
              onClick={goBack}
              style={{
                display: "inline-flex", alignItems: "center", gap: "8px",
                background: "linear-gradient(135deg, #6366f1, #4f46e5)",
                color: "#fff", border: "none", borderRadius: "10px",
                padding: "10px 24px", cursor: "pointer", fontWeight: 700, fontSize: "0.9rem",
              }}
            >
              <i className="bi bi-arrow-left"></i> Destek Taleplerine Dön
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!ticket) {
    return (
      <div style={{ textAlign: "center", padding: "80px 0" }}>
        <i className="bi bi-exclamation-triangle-fill" style={{ fontSize: "2.5rem", color: "#f59e0b" }}></i>
        <p style={{ color: "#6b7280", marginTop: "12px" }}>Ticket bulunamadı.</p>
        <button onClick={goBack} style={{ marginTop: "8px", background: "#2563eb", color: "#fff", border: "none", borderRadius: "8px", padding: "8px 20px", cursor: "pointer", fontWeight: 600 }}>
          Geri Dön
        </button>
      </div>
    );
  }

  const priority = PRIORITY[ticket.priority] ?? PRIORITY[1];
  const status   = getStatus(ticket.ticketStatusDescription);
  const isClosed = ticket.ticketStatusId === CLOSED_STATUS_ID;

  // ── Permission matrix ────────────────────────────────────────────────────────
  const isCreator    = currentUserId === ticket.mainCustomerId;
  const isAssignee   = !!ticket.customerId?.trim() && currentUserId === ticket.customerId;
  const isWatcher    = ticket.assignments?.some(a => a.assignedUserId === currentUserId) ?? false;
  const isUnassigned = !ticket.customerId?.trim();
  // Yaratıcı + atamasız → tam yetki (assignee gibi davranır)
  const effectiveAssignee = isAssignee || (isCreator && isUnassigned);
  const hasAnyRole   = isCreator || isAssignee || isWatcher;

  // Ticket kimseye assign edilmemiş ve kullanıcının rolü yoksa → önce Ticketi Al zorunlu
  const mustTakeFirst = isUnassigned && !isCreator && !isWatcher;

  const permissions = {
    canSendMessage:  !mustTakeFirst && hasAnyRole,
    canUpdateStatus: !mustTakeFirst && effectiveAssignee,
    canReassign:     !mustTakeFirst && effectiveAssignee,
    canEdit:         !mustTakeFirst && effectiveAssignee,
    canCloseTicket:  !mustTakeFirst && isCreator,
    canDelete:       !mustTakeFirst && isCreator,
    canAddWatchers:  !mustTakeFirst && effectiveAssignee,
    canTakeTicket:   mustTakeFirst && !!currentUserId && !isClosed && isInTicketDept,
    isReadOnly:      mustTakeFirst || !hasAnyRole,
  };
  // ─────────────────────────────────────────────────────────────────────────────

  // Mesaj listesi hem sayfada hem büyük görünüm popup'ında aynı şekilde çiziliyor.
  const renderMessages = (large: boolean) => (
    <>
        {/* Initial problem note */}
        {ticket.note && (
          <div style={{
            marginBottom: "20px",
            padding: "14px 16px",
            borderRadius: "12px",
            background: "linear-gradient(135deg, #fffbeb, #fef9c3)",
            border: "1.5px solid #fde68a",
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px" }}>
              <div style={{
                width: "32px", height: "32px", borderRadius: "50%",
                background: "#f59e0b", color: "#fff",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: "0.75rem", fontWeight: 700, flexShrink: 0,
              }}>
                {initials(ticket.mainCustomerName)}
              </div>
              <div>
                <div style={{ fontWeight: 700, fontSize: "0.85rem", color: "#92400e" }}>
                  {ticket.mainCustomerName}
                  <span style={{
                    marginLeft: "8px", fontSize: "0.68rem", fontWeight: 700,
                    background: "#fde68a", color: "#92400e",
                    padding: "2px 8px", borderRadius: "20px",
                  }}>Açılış Mesajı</span>
                </div>
                <div style={{ fontSize: "0.7rem", color: "#b45309" }}>{formatDate(ticket.createdAt)}</div>
              </div>
            </div>
            <div style={{
              fontSize: "0.88rem", color: "#78350f", lineHeight: 1.6,
              fontWeight: 500, paddingLeft: "40px",
            }}>
              {ticket.note}
            </div>
          </div>
        )}
        {messages.length === 0 ? (
          <div style={{ textAlign: "center", padding: "40px 0", color: "#9ca3af" }}>
            <i className="bi bi-chat-square" style={{ fontSize: "2rem" }}></i>
            <p style={{ marginTop: "8px", fontSize: "0.88rem" }}>Henüz mesaj yok. İlk mesajı siz gönderin!</p>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
            {messages.map((msg) => {
              if (msg.message.startsWith("@TicketBilgi ")) {
                const infoText = msg.message.slice("@TicketBilgi ".length);
                return (
                  <div key={msg.id} style={{ display: "flex", alignItems: "center", gap: "10px", padding: "2px 0" }}>
                    <div style={{ flex: 1, height: "1px", background: "#e5e7eb" }} />
                    <div style={{
                      display: "flex", alignItems: "center", gap: "6px",
                      background: "#f0f9ff", border: "1px solid #bae6fd",
                      borderRadius: "12px", padding: "6px 14px",
                      fontSize: "0.75rem", color: "#0369a1",
                      maxWidth: "80%", flexShrink: 1,
                    }}>
                      <i className="bi bi-info-circle-fill" style={{ flexShrink: 0, color: "#0891b2" }}></i>
                      <span style={{ lineHeight: 1.5 }}>{infoText}</span>
                      <span style={{ color: "#94a3b8", fontSize: "0.68rem", flexShrink: 0, marginLeft: "4px" }}>· {formatDate(msg.createdAt)}</span>
                    </div>
                    <div style={{ flex: 1, height: "1px", background: "#e5e7eb" }} />
                  </div>
                );
              }

              if (msg.message.startsWith("@todo ")) {
                const parsed = parseTodoMessage(msg.message);
                const isCompleted = parsed?.completed ?? true;
                const actorName = parsed
                  ? (msg.customerName || userList.find((u) => u.id === parsed.userId)?.name || parsed.userId)
                  : msg.customerName;
                const infoText = parsed
                  ? `${actorName} "${parsed.title}" görevini ${isCompleted ? "tamamladı" : "geri aldı"}`
                  : msg.message.slice("@todo ".length);
                return (
                  <div key={msg.id} style={{ display: "flex", alignItems: "center", gap: "10px", padding: "2px 0" }}>
                    <div style={{ flex: 1, height: "1px", background: "#e5e7eb" }} />
                    <div style={{
                      display: "flex", alignItems: "center", gap: "6px",
                      background: isCompleted ? "#f0fdf4" : "#fffbeb",
                      border: `1px solid ${isCompleted ? "#bbf7d0" : "#fde68a"}`,
                      borderRadius: "12px", padding: "6px 14px",
                      fontSize: "0.75rem", color: isCompleted ? "#166534" : "#92400e",
                      maxWidth: "80%", flexShrink: 1,
                    }}>
                      <i
                        className={`bi ${isCompleted ? "bi-check-circle-fill" : "bi-arrow-counterclockwise"}`}
                        style={{ flexShrink: 0, color: isCompleted ? "#16a34a" : "#d97706" }}
                      ></i>
                      <span style={{ lineHeight: 1.5 }}>{infoText}</span>
                      <span style={{ color: "#94a3b8", fontSize: "0.68rem", flexShrink: 0, marginLeft: "4px" }}>· {formatDate(msg.createdAt)}</span>
                    </div>
                    <div style={{ flex: 1, height: "1px", background: "#e5e7eb" }} />
                  </div>
                );
              }

              const isMe = msg.customerId === currentUserId;
              const color = avatarColor(msg.customerName);
              return (
                <div key={msg.id} style={{
                  display: "flex", flexDirection: isMe ? "row-reverse" : "row",
                  alignItems: "flex-start", gap: "10px",
                }}>
                  <div style={{
                    width: "36px", height: "36px", borderRadius: "50%",
                    background: color, color: "#fff",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: "0.75rem", fontWeight: 700, flexShrink: 0,
                  }}>
                    {initials(msg.customerName)}
                  </div>
                  <div style={{ maxWidth: large ? (isMobile ? "88%" : "80%") : "70%", minWidth: 0, overflow: "hidden" }}>
                    <div style={{
                      fontSize: "0.72rem", color: "#9ca3af", marginBottom: "4px",
                      textAlign: isMe ? "right" : "left",
                    }}>
                      <strong style={{ color: "#374151" }}>{isMe ? "Siz" : msg.customerName}</strong>
                      {" · "}{formatDate(msg.createdAt)}
                    </div>
                    <div style={{
                      padding: "10px 14px", borderRadius: isMe ? "14px 14px 4px 14px" : "14px 14px 14px 4px",
                      background: msg.bgColor ?? (isMe ? "#2563eb" : "#f3f4f6"),
                      color: msg.bgColor ? "#1f2937" : (isMe ? "#fff" : "#374151"),
                      fontSize: large ? "1rem" : "0.88rem", lineHeight: large ? 1.65 : 1.55,
                      boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
                      wordBreak: "break-word", overflowWrap: "anywhere",
                    }}>
                      <div style={{ all: "unset", display: "block" }} className={msg.bgColor ? "markdown-bubble" : (isMe ? "markdown-bubble-me" : "markdown-bubble")}>
                        <ReactMarkdown
                          remarkPlugins={[remarkGfm]}
                          rehypePlugins={[rehypeRaw]}
                          components={{
                            a: ({ href, children }) => {
                              const url = href && (href.startsWith('http') || href.startsWith('mailto'))
                                ? href
                                : (typeof children === 'string' && children.startsWith('http') ? children : href);
                              return <a href={url} target="_blank" rel="noopener noreferrer">{children}</a>;
                            },
                          }}
                        >{msg.message}</ReactMarkdown>
                      </div>
                      {msg.imageUrl && (
                        <img
                          src={msg.imageUrl}
                          alt="ek"
                          style={{
                            display: "block", marginTop: 8, maxWidth: "100%", maxHeight: large ? 480 : 220,
                            borderRadius: 8, cursor: "zoom-in",
                            border: "1px solid rgba(0,0,0,0.08)",
                          }}
                          onClick={() => setLightboxSrc(msg.imageUrl!)}
                        />
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
            {!large && <div ref={messagesEndRef} />}
          </div>
        )}
    </>
  );

  return (
    <>
    <div style={{ maxWidth: "1200px", margin: "0 auto", padding: isMobile ? "12px 10px" : "24px 16px" }}>

      {/* Edit Ticket Modal */}
      {showEdit && ticket && (
        <EditTicketModal
          isOpen={showEdit}
          onClose={() => setShowEdit(false)}
          ticket={ticket}
          onUpdated={() => {
            setSubmitting(true);
            setTimeout(() => {
              window.location.reload();
            }, 3000);
          }}
        />
      )}

      {/* Link Ticket Modal */}
      {showLinkModal && id && (
        <LinkTicketModal
          isOpen={showLinkModal}
          onClose={() => setShowLinkModal(false)}
          ticketId={id}
          onLinked={() => {
            setSubmitting(true);
            setTimeout(() => {
              window.location.reload();
            }, 1500);
          }}
        />
      )}

      {/* Linked Ticket Preview Modal */}
      {previewTicketId && (
        <ChildTicketPreviewModal
          ticketId={previewTicketId}
          onClose={() => setPreviewTicketId(null)}
        />
      )}

      {/* Full-page loading overlay */}
      {submitting && (
        <div style={{
          position: "fixed", inset: 0, zIndex: 2000,
          background: "rgba(255,255,255,0.75)", backdropFilter: "blur(4px)",
          display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "16px",
        }}>
          <div className="spinner-border text-primary" style={{ width: "48px", height: "48px" }} role="status" />
          <span style={{ fontWeight: 700, color: "#111827", fontSize: "0.95rem" }}>İşlemler tamamlanıyor...</span>
        </div>
      )}

      {/* Back */}
      <button
        onClick={goBack}
        style={{
          display: "inline-flex", alignItems: "center", gap: "6px",
          background: "none", border: "none", cursor: "pointer",
          color: "#6b7280", fontSize: "0.85rem", fontWeight: 600,
          marginBottom: "18px", padding: 0,
        }}
      >
        <i className="bi bi-arrow-left"></i> Destek Taleplerine Dön
      </button>

      {/* Closed banner */}
      {isClosed && (
        <div style={{
          marginBottom: "18px", padding: "14px 18px", borderRadius: "14px",
          background: "linear-gradient(135deg, #f1f5f9, #e2e8f0)",
          border: "1.5px solid #cbd5e1",
          display: "flex", alignItems: "center", gap: "12px",
        }}>
          <div style={{
            width: "40px", height: "40px", borderRadius: "50%", flexShrink: 0,
            background: "#e2e8f0", display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <i className="bi bi-lock-fill" style={{ fontSize: "1.1rem", color: "#64748b" }}></i>
          </div>
          <div>
            <div style={{ fontWeight: 800, color: "#1e293b", fontSize: "0.95rem" }}>Ticket Kapatılmış</div>
            <div style={{ fontSize: "0.8rem", color: "#64748b", marginTop: "2px" }}>
              Bu ticket kapatılmış olduğundan herhangi bir işlem yapılamaz.
            </div>
          </div>
        </div>
      )}

      {/* 3-column layout (mobile: single column) */}
      <div style={{
        display: "grid",
        gridTemplateColumns: isMobile ? "1fr" : "270px 1fr 270px",
        gap: "16px",
        alignItems: "start",
      }}>

        {/* ── LEFT: Ticket info ── */}
        <div>
          <div style={{
            background: "#fff", borderRadius: "16px",
            border: "1.5px solid #f1f5f9",
            boxShadow: "0 2px 12px rgba(0,0,0,0.07)",
            overflow: "hidden",
          }}>
            <div style={{ height: "5px", background: priority.color }} />
            <div style={{ padding: "18px" }}>
              {/* Ticket No */}
              {ticket.ticketNo && (
                <div style={{ fontSize: "0.7rem", fontWeight: 700, color: "#6366f1", marginBottom: "4px", letterSpacing: "0.03em" }}>
                  <i className="bi bi-hash me-1"></i>{ticket.ticketNo}
                </div>
              )}
              {/* Konu */}
              {ticket.ticketSubjectName && (
                <div style={{ fontSize: "0.75rem", fontWeight: 700, color: "#7c3aed", marginBottom: "8px", display: "flex", alignItems: "center", gap: "4px" }}>
                  <i className="bi bi-tag-fill"></i>{ticket.ticketSubjectName}
                </div>
              )}
              {/* Title */}
              <h5 style={{ fontWeight: 800, color: "#111827", margin: "0 0 10px", fontSize: "1rem", lineHeight: 1.4 }}>
                {ticket.title}
              </h5>
              {/* Badges */}
              <div style={{ display: "flex", gap: "6px", flexWrap: "wrap", marginBottom: "14px" }}>
                <span style={{
                  fontSize: "0.72rem", fontWeight: 700, padding: "3px 10px",
                  borderRadius: "20px", background: status.bg, color: status.color,
                  border: `1px solid ${status.border}`,
                }}>
                  {ticket.ticketStatusDescription}
                </span>
                <span style={{
                  fontSize: "0.72rem", fontWeight: 700, padding: "3px 10px",
                  borderRadius: "20px", background: priority.bg, color: priority.color,
                }}>
                  <i className="bi bi-flag-fill me-1"></i>{priority.label}
                </span>
              </div>
              {/* Meta list */}
              <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                {[
                  { icon: "bi-person-fill",       label: "Ticketi Yaratan", value: ticket.mainCustomerName },
                  { icon: "bi-person-check-fill", label: "Atanan Kişi",     value: ticket.customerName },
                  { icon: "bi-envelope-fill",     label: "E-posta",         value: ticket.mainCustomerEmail },
                  { icon: "bi-diagram-3-fill",    label: "Departman",       value: ticket.ticketDepartmentName },
                  { icon: "bi-calendar3",         label: "Oluşturuldu",     value: formatDate(ticket.createdAt) },
                  ...(ticket.updatedAt ? [{ icon: "bi-pencil-square", label: "Güncellendi", value: formatDate(ticket.updatedAt) }] : []),
                ].map((m) => (
                  <div key={m.label} style={{
                    display: "flex", alignItems: "flex-start", gap: "8px",
                    background: "#f8faff", borderRadius: "9px", padding: "9px 12px",
                  }}>
                    <i className={`bi ${m.icon}`} style={{ color: "#6366f1", fontSize: "0.9rem", flexShrink: 0, marginTop: "2px" }}></i>
                    <div>
                      <div style={{ fontSize: "0.65rem", color: "#9ca3af", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em" }}>{m.label}</div>
                      <div style={{ fontSize: "0.82rem", fontWeight: 600, color: "#374151", wordBreak: "break-word" }}>{m.value}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Görevler — özet, tıklayınca popup açılır */}
          {(!!ticket.tasks?.length || (!isClosed && permissions.canUpdateStatus)) && (
            <button
              type="button"
              onClick={() => setShowTasksPopup(true)}
              style={{
                display: "block", width: "100%", textAlign: "left",
                background: "#fff", borderRadius: "16px",
                border: "1.5px solid #e0e7ff",
                boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
                padding: "14px 16px",
                marginTop: 16, cursor: "pointer",
              }}
            >
              {ticket.tasks?.length ? (
                <>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "8px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <i className="bi bi-list-check" style={{ color: "#6366f1", fontSize: "0.95rem" }}></i>
                      <span style={{ fontWeight: 700, fontSize: "0.9rem", color: "#3730a3" }}>Görevler</span>
                      <span style={{
                        background: "#6366f1", color: "#fff",
                        fontSize: "0.68rem", fontWeight: 700,
                        borderRadius: "20px", padding: "2px 8px",
                        lineHeight: 1.4,
                        display: "inline-flex", alignItems: "center", justifyContent: "center",
                      }}>{ticket.completedTaskCount ?? 0}/{ticket.taskCount ?? ticket.tasks.length}</span>
                    </div>
                    <span style={{ fontSize: "0.82rem", fontWeight: 800, color: "#4f46e5" }}>%{ticket.progressPercent ?? 0}</span>
                  </div>
                  <div style={{ width: "100%", height: "8px", borderRadius: "20px", background: "#e5e7eb", overflow: "hidden" }}>
                    <div style={{
                      width: `${Math.min(100, Math.max(0, ticket.progressPercent ?? 0))}%`, height: "100%",
                      background: "linear-gradient(90deg, #6366f1, #4f46e5)", borderRadius: "20px",
                      transition: "width 0.3s",
                    }} />
                  </div>
                </>
              ) : (
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <i className="bi bi-list-check" style={{ color: "#6366f1", fontSize: "0.95rem" }}></i>
                  <span style={{ fontWeight: 700, fontSize: "0.9rem", color: "#3730a3" }}>Görevler</span>
                  <span style={{ fontSize: "0.8rem", color: "#9ca3af", marginLeft: "auto" }}>Görev ekle</span>
                </div>
              )}
            </button>
          )}

          {/* Dosya Ekle */}
          {ticket && id && (
            <div style={{
              background: "#fff", borderRadius: "16px",
              border: "1.5px solid #e0e7ff",
              boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
              overflow: "hidden",
              marginTop: 16,
            }}>
              <button
                type="button"
                onClick={() => setShowDocUpload(v => !v)}
                style={{
                  width: "100%", padding: "12px 16px",
                  background: "linear-gradient(135deg, #eef2ff, #e0e7ff)",
                  border: "none", cursor: "pointer",
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <i className="bi bi-paperclip" style={{ color: "#6366f1", fontSize: "0.95rem" }}></i>
                  <span style={{ fontWeight: 700, fontSize: "0.9rem", color: "#3730a3" }}>Dosyalar</span>
                  {docCount > 0 && (
                    <span style={{
                      background: "#6366f1", color: "#fff",
                      fontSize: "0.68rem", fontWeight: 700,
                      borderRadius: "20px", padding: "2px 8px",
                      lineHeight: 1.4,
                      display: "inline-flex", alignItems: "center", justifyContent: "center",
                    }}>{docCount}</span>
                  )}
                </div>
                <i className={`bi ${showDocUpload ? "bi-dash-lg" : "bi-plus-lg"}`} style={{ color: "#6366f1", fontWeight: 700 }}></i>
              </button>
              <div style={{ display: showDocUpload ? "block" : "none", padding: "12px" }}>
                <TicketDocumentUpload ticketId={id} disabled={isClosed} onDocCountChange={setDocCount} />
              </div>
            </div>
          )}

          {/* Ticketi Bağla */}
          {!mustTakeFirst && hasAnyRole && (
            <div style={{
              background: "#fff", borderRadius: "16px",
              border: "1.5px solid #e0e7ff",
              boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
              overflow: "hidden",
              marginTop: 16,
            }}>
              <button
                type="button"
                onClick={() => setShowLinkModal(true)}
                style={{
                  width: "100%", padding: "12px 16px",
                  background: "linear-gradient(135deg, #eef2ff, #e0e7ff)",
                  border: "none", cursor: "pointer",
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <i className="bi bi-link-45deg" style={{ color: "#4f46e5", fontSize: "1rem" }}></i>
                  <span style={{ fontWeight: 700, fontSize: "0.9rem", color: "#3730a3" }}>Ticketi Bağla</span>
                </div>
                <i className="bi bi-chevron-right" style={{ color: "#4f46e5", fontWeight: 700 }}></i>
              </button>
            </div>
          )}

          {/* Üst Ticket (parent) */}
          {ticket.parentTicketId && (
            <div style={{
              background: "#fff", borderRadius: "16px",
              border: "1.5px solid #e0e7ff",
              boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
              overflow: "hidden",
              marginTop: 16,
            }}>
              <div style={{
                padding: "12px 16px",
                background: "linear-gradient(135deg, #eef2ff, #e0e7ff)",
                display: "flex", alignItems: "center", gap: 8,
              }}>
                <i className="bi bi-diagram-3-fill" style={{ color: "#4f46e5", fontSize: "0.95rem" }}></i>
                <span style={{ fontWeight: 700, fontSize: "0.9rem", color: "#3730a3" }}>Bağlı Olduğu Üst Ticket</span>
              </div>
              <div style={{ padding: "10px 12px" }}>
                <button
                  type="button"
                  onClick={() => setPreviewTicketId(ticket.parentTicketId!)}
                  style={{
                    display: "flex", alignItems: "center", gap: 8,
                    padding: "8px 10px", borderRadius: 9,
                    background: "#eef2ff", border: "1px solid #e0e7ff",
                    cursor: "pointer", textAlign: "left", width: "100%",
                  }}
                >
                  {ticket.parentTicketNo && (
                    <span style={{ fontSize: "0.68rem", fontWeight: 700, color: "#4f46e5", flexShrink: 0 }}>
                      <i className="bi bi-hash"></i>{ticket.parentTicketNo}
                    </span>
                  )}
                  <span style={{
                    fontSize: "0.82rem", fontWeight: 600, color: "#3730a3",
                    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1, minWidth: 0,
                  }}>
                    {ticket.parentTicketTitle}
                  </span>
                  <i className="bi bi-chevron-right" style={{ color: "#4f46e5", fontSize: "0.75rem", flexShrink: 0 }}></i>
                </button>
              </div>
            </div>
          )}

          {/* Bağlı Ticketlar (alt kayıtlar) */}
          {children.length > 0 && (
            <div style={{
              background: "#fff", borderRadius: "16px",
              border: "1.5px solid #d1fae5",
              boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
              overflow: "hidden",
              marginTop: 16,
            }}>
              <div style={{
                padding: "12px 16px",
                background: "linear-gradient(135deg, #ecfdf5, #d1fae5)",
                display: "flex", alignItems: "center", gap: 8,
              }}>
                <i className="bi bi-diagram-2-fill" style={{ color: "#059669", fontSize: "0.95rem" }}></i>
                <span style={{ fontWeight: 700, fontSize: "0.9rem", color: "#065f46" }}>Bağlı Ticketlar</span>
                <span style={{
                  fontSize: "0.68rem", fontWeight: 700,
                  background: "#059669", color: "#fff",
                  borderRadius: "20px", padding: "2px 8px",
                }}>{children.length}</span>
              </div>
              <div style={{ padding: "10px 12px", display: "flex", flexDirection: "column", gap: 6 }}>
                {children.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => setPreviewTicketId(c.id)}
                    style={{
                      display: "flex", alignItems: "center", gap: 8,
                      padding: "8px 10px", borderRadius: 9,
                      background: "#f0fdf4", border: "1px solid #d1fae5",
                      cursor: "pointer", textAlign: "left", width: "100%",
                    }}
                  >
                    {c.ticketNo && (
                      <span style={{ fontSize: "0.68rem", fontWeight: 700, color: "#059669", flexShrink: 0 }}>
                        <i className="bi bi-hash"></i>{c.ticketNo}
                      </span>
                    )}
                    <span style={{
                      fontSize: "0.82rem", fontWeight: 600, color: "#065f46",
                      overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1, minWidth: 0,
                    }}>
                      {c.title}
                    </span>
                    <i className="bi bi-chevron-right" style={{ color: "#059669", fontSize: "0.75rem", flexShrink: 0 }}></i>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Ticketi Kapat */}
          {!isClosed && permissions.canCloseTicket && (
            <div style={{
              background: "#fff", borderRadius: "16px",
              border: "1.5px solid #fde68a",
              boxShadow: "0 2px 12px rgba(0,0,0,0.07)",
              overflow: "hidden", marginTop: "16px",
            }}>
              <div style={{
                padding: "12px 16px", borderBottom: "1.5px solid #fde68a",
                background: "linear-gradient(135deg, #fffbeb, #fef9c3)",
                display: "flex", alignItems: "center", justifyContent: "space-between",
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <i className="bi bi-lock-fill" style={{ color: "#d97706", fontSize: "0.95rem" }}></i>
                  <span style={{ fontWeight: 700, fontSize: "0.9rem", color: "#92400e" }}>Ticketi Kapat</span>
                </div>
                <button
                  onClick={() => { setShowClosePanel(!showClosePanel); setCloseMsg(""); setCloseError(""); }}
                  style={{
                    background: "none", border: "none", cursor: "pointer",
                    color: "#d97706", fontSize: "0.8rem", fontWeight: 600,
                    display: "flex", alignItems: "center", gap: "4px",
                  }}
                >
                  {showClosePanel ? "İptal" : <><i className="bi bi-chevron-down"></i></>}
                </button>
              </div>
              {!showClosePanel ? (
                <div style={{ padding: "12px 16px" }}>
                  <p style={{ margin: 0, fontSize: "0.8rem", color: "#6b7280" }}>
                    Ticketi kapatmak için bir kapanış mesajı bırakın.
                  </p>
                  <button
                    onClick={() => setShowClosePanel(true)}
                    style={{
                      marginTop: "10px", width: "100%", padding: "9px", borderRadius: "10px", border: "none",
                      background: "linear-gradient(135deg, #f59e0b, #d97706)", color: "#fff",
                      fontWeight: 700, fontSize: "0.85rem", cursor: "pointer",
                      display: "flex", alignItems: "center", justifyContent: "center", gap: "6px",
                    }}
                  >
                    <i className="bi bi-lock-fill"></i> Ticketi Kapat
                  </button>
                </div>
              ) : (
                <div style={{ padding: "14px 16px" }}>
                  {closeError && (
                    <div style={{
                      marginBottom: "10px", padding: "8px 12px", borderRadius: "9px", fontSize: "0.8rem",
                      background: "#fef2f2", border: "1px solid #fecaca", color: "#dc2626",
                      display: "flex", alignItems: "center", gap: "6px",
                    }}>
                      <i className="bi bi-exclamation-circle-fill"></i>{closeError}
                    </div>
                  )}
                  <div style={{
                    marginBottom: "10px", padding: "8px 12px", borderRadius: "9px", fontSize: "0.8rem",
                    background: "#fffbeb", border: "1px solid #fde68a", color: "#92400e",
                    display: "flex", alignItems: "flex-start", gap: "6px",
                  }}>
                    <i className="bi bi-exclamation-triangle-fill" style={{ color: "#f59e0b", flexShrink: 0, marginTop: "1px" }}></i>
                    Ticket kapatıldıktan sonra hiçbir işlem yapılamaz. Bu işlem geri alınamaz.
                  </div>
                  <textarea
                    rows={3}
                    placeholder="Kapanış mesajı yazın..."
                    value={closeMsg}
                    onChange={(e) => setCloseMsg(e.target.value)}
                    style={{
                      width: "100%", resize: "vertical",
                      border: "1.5px solid #fde68a", borderRadius: "10px",
                      padding: "9px 12px", fontSize: "0.85rem",
                      outline: "none", color: "#374151",
                      fontFamily: "inherit", boxSizing: "border-box",
                      marginBottom: "10px",
                    }}
                  />
                  <button
                    onClick={handleCloseTicket}
                    disabled={!closeMsg.trim() || closing}
                    style={{
                      width: "100%", padding: "9px", borderRadius: "10px", border: "none",
                      background: closeMsg.trim() && !closing ? "linear-gradient(135deg, #f59e0b, #d97706)" : "#e5e7eb",
                      color: closeMsg.trim() && !closing ? "#fff" : "#9ca3af",
                      fontWeight: 700, fontSize: "0.85rem",
                      cursor: closeMsg.trim() && !closing ? "pointer" : "not-allowed",
                      display: "flex", alignItems: "center", justifyContent: "center", gap: "6px",
                      transition: "background 0.15s",
                    }}
                  >
                    {closing
                      ? <><span className="spinner-border spinner-border-sm" role="status" /> Kapatılıyor...</>
                      : <><i className="bi bi-lock-fill"></i> Ticketi Kapat</>
                    }
                  </button>
                </div>
              )}
            </div>
          )}

        </div>

        {/* ── CENTER: Messages ── */}
        <div style={{
          background: "#fff", borderRadius: "16px",
          border: "1.5px solid #f1f5f9",
          boxShadow: "0 2px 12px rgba(0,0,0,0.07)",
          overflow: "hidden",
          display: "flex", flexDirection: "column",
        }}>
          {/* Header */}
          <div style={{
            padding: "14px 20px", borderBottom: "1.5px solid #f1f5f9",
            display: "flex", alignItems: "center", justifyContent: "space-between",
            flexShrink: 0,
          }}>
            <span style={{ fontWeight: 700, fontSize: "0.95rem", color: "#111827" }}>
              <i className="bi bi-chat-dots-fill me-2 text-primary"></i>Mesajlar
            </span>
            <span style={{
              fontSize: "0.72rem", fontWeight: 700,
              background: "#eff6ff", color: "#2563eb",
              padding: "2px 10px", borderRadius: "20px",
            }}>
              {messages.length} mesaj
            </span>
          </div>

          {/* Message list */}
          <div style={{ padding: "16px 20px", minHeight: isMobile ? "200px" : "300px", maxHeight: isMobile ? "380px" : "520px", overflowY: "auto", flex: 1 }}>
            {renderMessages(false)}
          </div>

          {/* Mesajları büyüt */}
          <div style={{ display: "flex", justifyContent: "flex-end", padding: "6px 20px", borderTop: "1.5px solid #f1f5f9", background: "#fff", flexShrink: 0 }}>
            <button
              type="button"
              onClick={() => setShowExpanded(true)}
              title="Mesajları büyük ekranda görüntüle"
              style={{
                display: "inline-flex", alignItems: "center", gap: 6,
                padding: "5px 12px", borderRadius: 8,
                border: "1.5px solid #e0e7ff", background: "#eef2ff",
                color: "#4f46e5", fontWeight: 600, fontSize: "0.78rem", cursor: "pointer",
              }}
            >
              <i className="bi bi-arrows-angle-expand"></i>Mesajları Büyüt
            </button>
          </div>

          {/* Reply box */}
          <div style={{ padding: "14px 20px", borderTop: "1.5px solid #f1f5f9", background: "#fafafa", flexShrink: 0 }}>
            {isClosed ? (
              <div style={{
                padding: "12px 16px", borderRadius: "12px",
                background: "#f1f5f9", border: "1.5px solid #cbd5e1",
                display: "flex", alignItems: "center", gap: "10px",
                color: "#64748b", fontSize: "0.85rem", fontWeight: 600,
              }}>
                <i className="bi bi-lock-fill" style={{ color: "#94a3b8" }}></i>
                Bu ticket kapatılmış. Mesaj gönderilemez.
              </div>
            ) : !permissions.canSendMessage ? (
              <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                {permissions.canTakeTicket ? (
                  <>
                    <div style={{
                      padding: "12px 16px", borderRadius: "12px",
                      background: "#eff6ff", border: "1.5px solid #bfdbfe",
                      display: "flex", alignItems: "center", gap: "10px",
                      color: "#1e40af", fontSize: "0.85rem", fontWeight: 600,
                    }}>
                      <i className="bi bi-person-plus-fill" style={{ color: "#2563eb" }}></i>
                      Bu ticket henüz kimseye atanmamış. Üzerine almak için butona tıklayın.
                    </div>
                    {takeError && (
                      <div style={{ padding: "8px 12px", borderRadius: "9px", fontSize: "0.8rem", background: "#fef2f2", border: "1px solid #fecaca", color: "#dc2626", display: "flex", alignItems: "center", gap: "6px" }}>
                        <i className="bi bi-exclamation-circle-fill"></i>{takeError}
                      </div>
                    )}
                    <button
                      onClick={handleSelfTake}
                      disabled={takingTicket}
                      style={{
                        width: "100%", padding: "12px", borderRadius: "12px", border: "none",
                        background: takingTicket ? "#e5e7eb" : "linear-gradient(135deg, #6366f1, #4f46e5)",
                        color: takingTicket ? "#9ca3af" : "#fff",
                        fontWeight: 700, fontSize: "0.92rem",
                        cursor: takingTicket ? "not-allowed" : "pointer",
                        display: "flex", alignItems: "center", justifyContent: "center", gap: "8px",
                        boxShadow: takingTicket ? "none" : "0 2px 14px rgba(99,102,241,0.4)",
                        transition: "background 0.15s",
                      }}
                    >
                      {takingTicket
                        ? <><span className="spinner-border spinner-border-sm" role="status" /> Alınıyor...</>
                        : <><i className="bi bi-person-check-fill"></i> Ticketi Al</>
                      }
                    </button>
                  </>
                ) : (
                  <div style={{
                    padding: "12px 16px", borderRadius: "12px",
                    background: "#fffbeb", border: "1.5px solid #fde68a",
                    display: "flex", alignItems: "center", gap: "10px",
                    color: "#92400e", fontSize: "0.85rem", fontWeight: 600,
                  }}>
                    <i className="bi bi-eye-fill" style={{ color: "#f59e0b" }}></i>
                    Bu ticketi sadece görüntülüyorsunuz. Mesaj gönderme yetkiniz yok.
                  </div>
                )}
              </div>
            ) : (
              <>
                {sendError && (
                  <div style={{ marginBottom: "8px", color: "#dc2626", fontSize: "0.8rem" }}>
                    <i className="bi bi-exclamation-circle me-1"></i>{sendError}
                  </div>
                )}
                <RichTextEditor
                  value={newMsg}
                  onChange={setNewMsg}
                  onKeyDown={handleKeyDown}
                  placeholder="Mesajınızı yazın... (Ctrl+Enter ile gönderebilirsiniz)"
                  image={msgImage}
                  onImageChange={setMsgImage}
                  disabled={sending}
                  minRows={4}
                  bgColor={msgBgColor || undefined}
                />
                {canSetMessageColor && (
                  <div style={{ display: "flex", alignItems: "center", gap: "8px", marginTop: "10px", flexWrap: "wrap" }}>
                    <span style={{ fontSize: "0.74rem", color: "#6b7280", fontWeight: 600 }}>
                      <i className="bi bi-palette-fill me-1"></i>Vurgu Rengi:
                    </span>
                    <button
                      type="button"
                      onClick={() => setMsgBgColor("")}
                      title="Renksiz"
                      style={{
                        width: "22px", height: "22px", borderRadius: "50%", cursor: "pointer",
                        background: "#fff",
                        border: msgBgColor === "" ? "2px solid #2563eb" : "1.5px solid #d1d5db",
                        display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                      }}
                    >
                      <i className="bi bi-slash-lg" style={{ fontSize: "0.65rem", color: "#9ca3af" }}></i>
                    </button>
                    {MESSAGE_COLOR_PALETTE.map((c) => (
                      <button
                        key={c.value}
                        type="button"
                        onClick={() => setMsgBgColor(c.value)}
                        title={c.label}
                        style={{
                          width: "22px", height: "22px", borderRadius: "50%", cursor: "pointer",
                          background: c.value,
                          border: msgBgColor === c.value ? "2px solid #2563eb" : "1.5px solid rgba(0,0,0,0.08)",
                          flexShrink: 0,
                        }}
                      />
                    ))}
                  </div>
                )}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "8px" }}>
                  <div style={{ fontSize: "0.72rem", color: "#9ca3af" }}>
                    <i className="bi bi-info-circle me-1"></i>
                    Enter yeni satır · Ctrl+Enter gönder · Ctrl+B kalın · Ctrl+I italik
                  </div>
                  <button
                    onClick={handleSend}
                    disabled={!newMsg.trim() || sending}
                    style={{
                      padding: "10px 18px", borderRadius: "12px", border: "none",
                      background: newMsg.trim() && !sending ? "#2563eb" : "#e5e7eb",
                      color: newMsg.trim() && !sending ? "#fff" : "#9ca3af",
                      fontWeight: 700, fontSize: "0.88rem",
                      cursor: newMsg.trim() && !sending ? "pointer" : "not-allowed",
                      display: "flex", alignItems: "center", gap: "6px",
                      transition: "background 0.15s",
                    }}
                  >
                    {sending ? (
                      <span className="spinner-border spinner-border-sm" role="status" />
                    ) : (
                      <><i className="bi bi-send-fill"></i> Gönder</>
                    )}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>

        {/* ── RIGHT: Actions ── */}
        <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>

          {mustTakeFirst && !isClosed && (
            <div style={{
              background: "#fff", borderRadius: "16px",
              border: "1.5px solid #bfdbfe",
              boxShadow: "0 2px 12px rgba(0,0,0,0.07)",
              padding: "18px 16px",
              display: "flex", flexDirection: "column", alignItems: "center", gap: "10px",
              textAlign: "center",
            }}>
              <div style={{
                width: "48px", height: "48px", borderRadius: "50%",
                background: "#eff6ff", display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                <i className="bi bi-person-plus-fill" style={{ fontSize: "1.3rem", color: "#2563eb" }}></i>
              </div>
              <div style={{ fontWeight: 700, color: "#1e40af", fontSize: "0.9rem" }}>Ticket Sahipsiz</div>
              <div style={{ fontSize: "0.78rem", color: "#94a3b8", lineHeight: 1.5 }}>
                Bu ticket henüz kimseye atanmamış. İşlem yapabilmek için önce mesaj alanından ticketi üzerinize alın.
              </div>
            </div>
          )}

          {isClosed && (
            <div style={{
              background: "#fff", borderRadius: "16px",
              border: "1.5px solid #cbd5e1",
              boxShadow: "0 2px 12px rgba(0,0,0,0.07)",
              padding: "18px 16px",
              display: "flex", flexDirection: "column", alignItems: "center", gap: "10px",
              textAlign: "center",
            }}>
              <div style={{
                width: "48px", height: "48px", borderRadius: "50%",
                background: "#e2e8f0", display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                <i className="bi bi-lock-fill" style={{ fontSize: "1.3rem", color: "#64748b" }}></i>
              </div>
              <div style={{ fontWeight: 700, color: "#334155", fontSize: "0.9rem" }}>İşlem Yapılamaz</div>
              <div style={{ fontSize: "0.78rem", color: "#94a3b8", lineHeight: 1.5 }}>
                Ticket kapatıldığından durum güncelleme, atama ve düzenleme işlemleri devre dışı.
              </div>
            </div>
          )}

          {/* Status update */}
          {!isClosed && permissions.canUpdateStatus && <div style={{
            background: "#fff", borderRadius: "16px",
            border: "1.5px solid #f1f5f9",
            boxShadow: "0 2px 12px rgba(0,0,0,0.07)",
            overflow: "hidden",
          }}>
            <div style={{ padding: "12px 16px", borderBottom: "1.5px solid #f1f5f9", display: "flex", alignItems: "center", gap: "8px" }}>
              <i className="bi bi-arrow-repeat" style={{ color: "#0891b2", fontSize: "0.95rem" }}></i>
              <span style={{ fontWeight: 700, fontSize: "0.9rem", color: "#111827" }}>Durum Güncelle</span>
            </div>
            <div style={{ padding: "14px 16px" }}>
              {statusMsg && (
                <div style={{
                  marginBottom: "10px", padding: "8px 12px", borderRadius: "9px", fontSize: "0.8rem",
                  background: statusMsg.type === "success" ? "#f0fdf4" : "#fef2f2",
                  border: `1px solid ${statusMsg.type === "success" ? "#bbf7d0" : "#fecaca"}`,
                  color: statusMsg.type === "success" ? "#166534" : "#dc2626",
                  display: "flex", alignItems: "center", gap: "6px",
                }}>
                  <i className={`bi ${statusMsg.type === "success" ? "bi-check-circle-fill" : "bi-exclamation-circle-fill"}`}></i>
                  {statusMsg.text}
                </div>
              )}
              <select
                value={selectedStatusId}
                onChange={(e) => setSelectedStatusId(e.target.value)}
                style={{
                  width: "100%", padding: "9px 12px", marginBottom: "8px",
                  border: "1.5px solid #e5e7eb", borderRadius: "10px",
                  fontSize: "0.85rem", color: "#374151", outline: "none",
                  background: "#fafafa", cursor: "pointer",
                }}
              >
                <option value="">— Durum seçin —</option>
                {statusList.filter((s) => s.description !== "Ticket yaratıldı" && s.id !== CLOSED_STATUS_ID && s.id !== ticket.ticketStatusId).map((s) => (
                  <option key={s.id} value={s.id}>{s.description}</option>
                ))}
              </select>
              <button
                onClick={handleStatusUpdate}
                disabled={!selectedStatusId || updatingStatus}
                style={{
                  width: "100%", padding: "9px", borderRadius: "10px", border: "none",
                  background: selectedStatusId && !updatingStatus ? "#0891b2" : "#e5e7eb",
                  color: selectedStatusId && !updatingStatus ? "#fff" : "#9ca3af",
                  fontWeight: 700, fontSize: "0.85rem",
                  cursor: selectedStatusId && !updatingStatus ? "pointer" : "not-allowed",
                  display: "flex", alignItems: "center", justifyContent: "center", gap: "6px",
                  transition: "background 0.15s",
                }}
              >
                {updatingStatus
                  ? <><span className="spinner-border spinner-border-sm" role="status" /> Güncelleniyor...</>
                  : <><i className="bi bi-check2-circle"></i> Değiştir</>
                }
              </button>
              <div style={{ marginTop: "6px", fontSize: "0.72rem", color: "#9ca3af" }}>
                <i className="bi bi-info-circle me-1"></i>
                Mevcut: <strong style={{ color: "#0891b2" }}>{ticket.ticketStatusDescription}</strong>
              </div>
            </div>
          </div>}

          {/* Reassign */}
          {!isClosed && permissions.canReassign && <div style={{
            background: "#fff", borderRadius: "16px",
            border: "1.5px solid #f1f5f9",
            boxShadow: "0 2px 12px rgba(0,0,0,0.07)",
            overflow: "hidden",
          }}>
            <div style={{ padding: "12px 16px", borderBottom: "1.5px solid #f1f5f9", display: "flex", alignItems: "center", gap: "8px" }}>
              <i className="bi bi-person-fill-gear" style={{ color: "#6366f1", fontSize: "0.95rem" }}></i>
              <span style={{ fontWeight: 700, fontSize: "0.9rem", color: "#111827" }}>Kişiye Ata</span>
            </div>
            <div style={{ padding: "14px 16px" }}>
              {reassignMsg && (
                <div style={{
                  marginBottom: "10px", padding: "8px 12px", borderRadius: "9px", fontSize: "0.8rem",
                  background: reassignMsg.type === "success" ? "#f0fdf4" : "#fef2f2",
                  border: `1px solid ${reassignMsg.type === "success" ? "#bbf7d0" : "#fecaca"}`,
                  color: reassignMsg.type === "success" ? "#166534" : "#dc2626",
                  display: "flex", alignItems: "center", gap: "6px",
                }}>
                  <i className={`bi ${reassignMsg.type === "success" ? "bi-check-circle-fill" : "bi-exclamation-circle-fill"}`}></i>
                  {reassignMsg.text}
                </div>
              )}
              <select
                value={selectedUserId}
                onChange={(e) => setSelectedUserId(e.target.value)}
                style={{
                  width: "100%", padding: "9px 12px", marginBottom: "8px",
                  border: "1.5px solid #e5e7eb", borderRadius: "10px",
                  fontSize: "0.85rem", color: "#374151", outline: "none",
                  background: "#fafafa", cursor: "pointer",
                }}
              >
                <option value="">— Kullanıcı seçin —</option>
                {userList.filter((u) => u.id !== ticket.customerId).map((u) => (
                  <option key={u.id} value={u.id}>{u.name}</option>
                ))}
              </select>
              <button
                onClick={handleReassign}
                disabled={!selectedUserId || reassigning}
                style={{
                  width: "100%", padding: "9px", borderRadius: "10px", border: "none",
                  background: selectedUserId && !reassigning ? "#6366f1" : "#e5e7eb",
                  color: selectedUserId && !reassigning ? "#fff" : "#9ca3af",
                  fontWeight: 700, fontSize: "0.85rem",
                  cursor: selectedUserId && !reassigning ? "pointer" : "not-allowed",
                  display: "flex", alignItems: "center", justifyContent: "center", gap: "6px",
                  transition: "background 0.15s",
                }}
              >
                {reassigning
                  ? <><span className="spinner-border spinner-border-sm" role="status" /> Atanıyor...</>
                  : <><i className="bi bi-person-check-fill"></i> Ata</>
                }
              </button>
              {ticket.customerName && (
                <div style={{ marginTop: "6px", fontSize: "0.72rem", color: "#9ca3af" }}>
                  <i className="bi bi-info-circle me-1"></i>
                  Şu an: <strong style={{ color: "#6366f1" }}>{ticket.customerName}</strong>
                </div>
              )}
            </div>
          </div>}

          {/* İzleyiciler */}
          <div style={{
            background: "#fff", borderRadius: "16px",
            border: "1.5px solid #ede9fe",
            boxShadow: "0 2px 12px rgba(0,0,0,0.07)",
            overflow: "hidden",
          }}>
            <div style={{ padding: "12px 16px", borderBottom: "1.5px solid #ede9fe", background: "linear-gradient(135deg,#faf5ff,#f3e8ff)", display: "flex", alignItems: "center", gap: "8px" }}>
              <i className="bi bi-people-fill" style={{ color: "#7c3aed", fontSize: "0.95rem" }}></i>
              <span style={{ fontWeight: 700, fontSize: "0.9rem", color: "#111827" }}>İzleyiciler</span>
              {ticket.assignments?.length > 0 && (
                <span style={{
                  fontSize: "0.72rem", fontWeight: 700,
                  background: "#ede9fe", color: "#7c3aed",
                  padding: "2px 9px", borderRadius: "20px",
                }}>{ticket.assignments.length}</span>
              )}
              {permissions.canAddWatchers && (
                <button
                  onClick={() => { setShowWatchersPopup(true); setSelectedWatchers([]); setWatchersError(""); }}
                  style={{
                    marginLeft: "auto", width: "26px", height: "26px", borderRadius: "8px",
                    border: "none", background: "#7c3aed", color: "#fff",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    cursor: "pointer", fontSize: "0.85rem", flexShrink: 0,
                  }}
                  title="İzleyici ekle"
                >
                  <i className="bi bi-plus-lg"></i>
                </button>
              )}
            </div>
            <div style={{ padding: "12px 16px" }}>
              {!ticket.assignments || ticket.assignments.length === 0 ? (
                <div style={{ textAlign: "center", padding: "16px 0", color: "#9ca3af" }}>
                  <i className="bi bi-person-x" style={{ fontSize: "1.6rem", display: "block", marginBottom: "6px", opacity: 0.4 }}></i>
                  <span style={{ fontSize: "0.8rem" }}>Henüz izleyici yok</span>
                </div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                  {ticket.assignments.map((a: ITicketAssignment) => (
                    <div key={a.id} style={{
                      display: "flex", alignItems: "center", gap: "10px",
                      padding: "9px 12px", borderRadius: "10px",
                      background: "#faf5ff", border: "1px solid #ede9fe",
                    }}>
                      <div style={{
                        width: "34px", height: "34px", borderRadius: "50%",
                        background: avatarColor(a.assignedUserName), color: "#fff",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        fontSize: "0.72rem", fontWeight: 700, flexShrink: 0,
                      }}>
                        {initials(a.assignedUserName)}
                      </div>
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div style={{ fontWeight: 700, fontSize: "0.82rem", color: "#374151", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {a.assignedUserName}
                        </div>
                        <div style={{ fontSize: "0.7rem", color: "#9ca3af", marginTop: "1px" }}>
                          <i className="bi bi-person-fill me-1" style={{ color: "#a78bfa" }}></i>
                          {a.assignedByName} · {formatDate(a.assignedAt)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* İzleyici ekle popup */}
          {showWatchersPopup && (() => {
            const excludedIds = new Set([
              ticket.mainCustomerId,
              ticket.customerId,
              ...(ticket.assignments?.map(a => a.assignedUserId) ?? []),
            ]);
            const available = userList.filter(u => !excludedIds.has(u.id));
            const toggleUser = (uid: string) =>
              setSelectedWatchers(prev =>
                prev.includes(uid) ? prev.filter(x => x !== uid) : [...prev, uid]
              );
            const handleAdd = async () => {
              if (!id || selectedWatchers.length === 0) return;
              setAddingWatchers(true);
              setWatchersError("");
              try {
                await assignUsersToTicket(id, selectedWatchers);
                setShowWatchersPopup(false);
                await reloadTicket();
              } catch {
                setWatchersError("Kullanıcılar eklenemedi. Lütfen tekrar deneyin.");
              } finally {
                setAddingWatchers(false);
              }
            };
            return (
              <div
                onClick={() => setShowWatchersPopup(false)}
                style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 3000, display: "flex", alignItems: "center", justifyContent: "center", padding: "16px" }}
              >
                <div onClick={e => e.stopPropagation()} style={{ background: "#fff", borderRadius: "18px", width: "100%", maxWidth: "400px", boxShadow: "0 8px 40px rgba(0,0,0,0.22)", overflow: "hidden" }}>
                  {/* Header */}
                  <div style={{ padding: "16px 20px", borderBottom: "1.5px solid #ede9fe", background: "linear-gradient(135deg,#faf5ff,#f3e8ff)", display: "flex", alignItems: "center", gap: "10px" }}>
                    <i className="bi bi-person-plus-fill" style={{ color: "#7c3aed", fontSize: "1.1rem" }}></i>
                    <span style={{ fontWeight: 800, fontSize: "0.95rem", color: "#111827" }}>İzleyici Ekle</span>
                    <button
                      onClick={() => setShowWatchersPopup(false)}
                      style={{ marginLeft: "auto", border: "none", background: "transparent", cursor: "pointer", color: "#9ca3af", fontSize: "1rem" }}
                    >
                      <i className="bi bi-x-lg"></i>
                    </button>
                  </div>
                  {/* User list */}
                  <div style={{ padding: "12px 16px", maxHeight: "320px", overflowY: "auto" }}>
                    {available.length === 0 ? (
                      <div style={{ textAlign: "center", padding: "32px 0", color: "#9ca3af" }}>
                        <i className="bi bi-people" style={{ fontSize: "2rem", display: "block", marginBottom: "8px", opacity: 0.4 }}></i>
                        <span style={{ fontSize: "0.82rem" }}>Eklenebilecek kullanıcı yok</span>
                      </div>
                    ) : (
                      <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                        {available.map(u => {
                          const checked = selectedWatchers.includes(u.id);
                          return (
                            <label key={u.id} style={{
                              display: "flex", alignItems: "center", gap: "12px",
                              padding: "10px 12px", borderRadius: "10px", cursor: "pointer",
                              background: checked ? "#faf5ff" : "#f9fafb",
                              border: `1.5px solid ${checked ? "#a78bfa" : "#f1f5f9"}`,
                              transition: "background 0.15s, border-color 0.15s",
                            }}>
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => toggleUser(u.id)}
                                style={{ width: "17px", height: "17px", accentColor: "#7c3aed", cursor: "pointer", flexShrink: 0 }}
                              />
                              <div style={{
                                width: "32px", height: "32px", borderRadius: "50%",
                                background: avatarColor(u.name), color: "#fff",
                                display: "flex", alignItems: "center", justifyContent: "center",
                                fontSize: "0.7rem", fontWeight: 700, flexShrink: 0,
                              }}>
                                {initials(u.name)}
                              </div>
                              <span style={{ fontWeight: 600, fontSize: "0.85rem", color: "#374151" }}>{u.name}</span>
                            </label>
                          );
                        })}
                      </div>
                    )}
                  </div>
                  {/* Footer */}
                  <div style={{ padding: "12px 16px", borderTop: "1.5px solid #f1f5f9", background: "#fafafa" }}>
                    {watchersError && (
                      <div style={{ marginBottom: "10px", padding: "8px 12px", borderRadius: "9px", fontSize: "0.8rem", background: "#fef2f2", border: "1px solid #fecaca", color: "#dc2626", display: "flex", alignItems: "center", gap: "6px" }}>
                        <i className="bi bi-exclamation-circle-fill"></i>{watchersError}
                      </div>
                    )}
                    <div style={{ display: "flex", gap: "8px" }}>
                      <button
                        onClick={() => setShowWatchersPopup(false)}
                        style={{ flex: 1, padding: "10px", borderRadius: "10px", border: "1.5px solid #e5e7eb", background: "#fff", color: "#374151", fontWeight: 600, fontSize: "0.85rem", cursor: "pointer" }}
                      >
                        İptal
                      </button>
                      <button
                        onClick={handleAdd}
                        disabled={selectedWatchers.length === 0 || addingWatchers}
                        style={{
                          flex: 2, padding: "10px", borderRadius: "10px", border: "none",
                          background: selectedWatchers.length > 0 && !addingWatchers ? "#7c3aed" : "#e5e7eb",
                          color: selectedWatchers.length > 0 && !addingWatchers ? "#fff" : "#9ca3af",
                          fontWeight: 700, fontSize: "0.85rem",
                          cursor: selectedWatchers.length > 0 && !addingWatchers ? "pointer" : "not-allowed",
                          display: "flex", alignItems: "center", justifyContent: "center", gap: "6px",
                        }}
                      >
                        {addingWatchers
                          ? <><span className="spinner-border spinner-border-sm" role="status" /> Ekleniyor...</>
                          : <><i className="bi bi-person-check-fill"></i> Tickete Ekle {selectedWatchers.length > 0 && `(${selectedWatchers.length})`}</>
                        }
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Edit */}
          {!isClosed && permissions.canEdit && <div style={{
            background: "#fff", borderRadius: "16px",
            border: "1.5px solid #f1f5f9",
            boxShadow: "0 2px 12px rgba(0,0,0,0.07)",
            overflow: "hidden",
          }}>
            <div style={{ padding: "12px 16px", borderBottom: "1.5px solid #f1f5f9", display: "flex", alignItems: "center", gap: "8px" }}>
              <i className="bi bi-pencil-square" style={{ color: "#0891b2", fontSize: "0.95rem" }}></i>
              <span style={{ fontWeight: 700, fontSize: "0.9rem", color: "#111827" }}>Ticket Güncelle</span>
            </div>
            <div style={{ padding: "14px 16px" }}>
              <p style={{ margin: "0 0 10px", fontSize: "0.8rem", color: "#6b7280" }}>
                {ticket.messageCount > 0
                  ? "Mesaj olduğu için sadece departman değiştirilebilir."
                  : "Başlık, açıklama ve departmanı düzenleyebilirsiniz."}
              </p>
              <button
                onClick={() => setShowEdit(true)}
                style={{
                  width: "100%", padding: "9px", borderRadius: "10px", border: "none",
                  background: "#f0f9ff", color: "#0891b2",
                  fontWeight: 700, fontSize: "0.85rem", cursor: "pointer",
                  display: "flex", alignItems: "center", justifyContent: "center", gap: "6px",
                }}
              >
                <i className="bi bi-pencil-square"></i> Düzenle
              </button>
            </div>
          </div>}

          {/* Delete */}
          {permissions.canDelete && <div style={{
            background: "#fff", borderRadius: "16px",
            border: "1.5px solid #fee2e2",
            boxShadow: "0 2px 12px rgba(0,0,0,0.07)",
            overflow: "hidden",
          }}>
            <div style={{ padding: "12px 16px", borderBottom: "1.5px solid #fee2e2", display: "flex", alignItems: "center", gap: "8px" }}>
              <i className="bi bi-trash3-fill" style={{ color: "#dc2626", fontSize: "0.95rem" }}></i>
              <span style={{ fontWeight: 700, fontSize: "0.9rem", color: "#111827" }}>Ticketi Sil</span>
            </div>
            <div style={{ padding: "14px 16px" }}>
              {deleteError && (
                <div style={{
                  marginBottom: "10px", padding: "8px 12px", borderRadius: "9px", fontSize: "0.8rem",
                  background: "#fef2f2", border: "1px solid #fecaca", color: "#dc2626",
                  display: "flex", alignItems: "center", gap: "6px",
                }}>
                  <i className="bi bi-exclamation-circle-fill"></i>{deleteError}
                </div>
              )}
              {!confirmDelete ? (
                <>
                  <p style={{ margin: "0 0 10px", fontSize: "0.8rem", color: "#6b7280" }}>
                    Bu ticket kalıcı olarak silinecek. Bu işlem geri alınamaz.
                  </p>
                  <button
                    onClick={() => setConfirmDelete(true)}
                    style={{
                      width: "100%", padding: "9px", borderRadius: "10px", border: "none",
                      background: "#fef2f2", color: "#dc2626",
                      fontWeight: 700, fontSize: "0.85rem", cursor: "pointer",
                      display: "flex", alignItems: "center", justifyContent: "center", gap: "6px",
                      transition: "background 0.15s",
                    }}
                  >
                    <i className="bi bi-trash3-fill"></i> Ticketi Sil
                  </button>
                </>
              ) : (
                <>
                  <div style={{
                    padding: "10px 12px", borderRadius: "9px",
                    background: "#fff7ed", border: "1.5px solid #fed7aa",
                    color: "#92400e", fontSize: "0.8rem", marginBottom: "12px",
                  }}>
                    <i className="bi bi-exclamation-triangle-fill me-1" style={{ color: "#f59e0b" }}></i>
                    <strong>Emin misiniz?</strong> Bu işlem geri alınamaz.
                  </div>
                  <div style={{ display: "flex", gap: "8px" }}>
                    <button
                      onClick={() => { setConfirmDelete(false); setDeleteError(""); }}
                      disabled={deleting}
                      style={{
                        flex: 1, padding: "9px", borderRadius: "10px",
                        border: "1.5px solid #e5e7eb", background: "#fff",
                        color: "#374151", fontWeight: 600, fontSize: "0.82rem",
                        cursor: "pointer",
                      }}
                    >
                      İptal
                    </button>
                    <button
                      onClick={handleDelete}
                      disabled={deleting}
                      style={{
                        flex: 1, padding: "9px", borderRadius: "10px", border: "none",
                        background: deleting ? "#e5e7eb" : "#dc2626",
                        color: deleting ? "#9ca3af" : "#fff",
                        fontWeight: 700, fontSize: "0.82rem",
                        cursor: deleting ? "not-allowed" : "pointer",
                        display: "flex", alignItems: "center", justifyContent: "center", gap: "6px",
                        transition: "background 0.15s",
                      }}
                    >
                      {deleting
                        ? <><span className="spinner-border spinner-border-sm" role="status" /> Siliniyor...</>
                        : <><i className="bi bi-trash3-fill"></i> Evet, Sil</>
                      }
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>}

        </div>
      </div>
    </div>

    {showTasksPopup && ticket && (
      <div
        onClick={() => setShowTasksPopup(false)}
        style={{
          position: "fixed", inset: 0,
          background: "rgba(0,0,0,0.55)",
          zIndex: 2000,
          display: "flex", alignItems: "center", justifyContent: "center",
          padding: "16px",
        }}
      >
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            background: "#fff", borderRadius: "20px",
            width: "92%", maxWidth: "480px",
            maxHeight: "80vh", display: "flex", flexDirection: "column",
            boxShadow: "0 12px 48px rgba(0,0,0,0.22)",
            overflow: "hidden",
          }}
        >
          {/* Header */}
          <div style={{
            padding: "18px 22px 16px", borderBottom: "1.5px solid #f1f5f9",
            display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0,
          }}>
            <div style={{ fontWeight: 800, fontSize: "0.98rem", color: "#111827", display: "flex", alignItems: "center", gap: "8px" }}>
              <i className="bi bi-list-check" style={{ color: "#6366f1" }}></i>
              Görevler
            </div>
            <button
              onClick={() => setShowTasksPopup(false)}
              style={{
                background: "#f3f4f6", border: "none",
                borderRadius: "8px", width: "30px", height: "30px",
                display: "flex", alignItems: "center", justifyContent: "center",
                cursor: "pointer", color: "#6b7280", fontSize: "1.05rem",
              }}
            >×</button>
          </div>

          <div style={{ padding: "16px 22px", overflowY: "auto", flex: 1 }}>
            {taskError && (
              <div style={{
                marginBottom: "12px", padding: "8px 12px", borderRadius: "9px", fontSize: "0.8rem",
                background: "#fef2f2", border: "1px solid #fecaca", color: "#dc2626",
                display: "flex", alignItems: "center", gap: "6px",
              }}>
                <i className="bi bi-exclamation-circle-fill"></i>{taskError}
              </div>
            )}
            <div style={{ marginBottom: "14px" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "6px" }}>
                <span style={{ fontSize: "0.78rem", fontWeight: 600, color: "#374151" }}>İlerleme</span>
                <span style={{ fontSize: "0.82rem", fontWeight: 800, color: "#4f46e5" }}>%{ticket.progressPercent ?? 0}</span>
              </div>
              <div style={{ width: "100%", height: "8px", borderRadius: "20px", background: "#e5e7eb", overflow: "hidden" }}>
                <div style={{
                  width: `${Math.min(100, Math.max(0, ticket.progressPercent ?? 0))}%`, height: "100%",
                  background: "linear-gradient(90deg, #6366f1, #4f46e5)", borderRadius: "20px",
                  transition: "width 0.3s",
                }} />
              </div>
            </div>

            {!isClosed && permissions.canUpdateStatus && (
              <div style={{ marginBottom: "14px" }}>
              <div style={{ display: "flex", gap: "8px" }}>
                <input
                  type="text"
                  value={newTaskTitle}
                  onChange={(e) => setNewTaskTitle(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handleAddTask(); } }}
                  placeholder="Görev ekle..."
                  maxLength={200}
                  disabled={addingTask}
                  style={{
                    flex: 1, minWidth: 0, padding: "9px 12px", boxSizing: "border-box",
                    border: "1.5px solid #e5e7eb", borderRadius: "10px",
                    fontSize: "0.88rem", outline: "none", color: "#111827",
                  }}
                />
                <input
                  type="number"
                  min={1}
                  value={newTaskWeight}
                  onChange={(e) => setNewTaskWeight(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handleAddTask(); } }}
                  placeholder="Ağırlık"
                  title="Görev Ağırlığı — bu görevin ticketin tamamlanma yüzdesine ne kadar katkı yapacağını belirler"
                  disabled={addingTask}
                  style={{
                    width: "78px", flexShrink: 0, padding: "9px 8px", boxSizing: "border-box",
                    border: "1.5px solid #e5e7eb", borderRadius: "10px",
                    fontSize: "0.88rem", outline: "none", color: "#111827", textAlign: "center",
                  }}
                />
                <button
                  type="button"
                  onClick={handleAddTask}
                  disabled={!newTaskTitle.trim() || addingTask}
                  style={{
                    padding: "0 16px", borderRadius: "10px", border: "none",
                    background: newTaskTitle.trim() && !addingTask ? "#eef2ff" : "#f3f4f6",
                    color: newTaskTitle.trim() && !addingTask ? "#4f46e5" : "#9ca3af",
                    fontWeight: 700, fontSize: "0.85rem",
                    cursor: newTaskTitle.trim() && !addingTask ? "pointer" : "not-allowed",
                    display: "flex", alignItems: "center", gap: "6px", whiteSpace: "nowrap", flexShrink: 0,
                  }}
                >
                  {addingTask
                    ? <span className="spinner-border spinner-border-sm" role="status" />
                    : <i className="bi bi-plus-lg"></i>
                  }
                  Ekle
                </button>
              </div>
              <div style={{ marginTop: "6px", display: "flex", alignItems: "flex-start", gap: "5px", fontSize: "0.72rem", color: "#9ca3af", lineHeight: 1.4 }}>
                <i className="bi bi-info-circle" style={{ flexShrink: 0, marginTop: "1px" }}></i>
                <span>Ağırlık: Bu görevin, ticketin tamamlanma yüzdesine ne kadar katkı yapacağını belirler. Ne kadar yüksekse etkisi o kadar fazla olur (varsayılan: 100).</span>
              </div>
              </div>
            )}

            {!ticket.tasks?.length && (
              <div style={{ textAlign: "center", color: "#9ca3af", padding: "20px 0", fontSize: "0.85rem" }}>
                <i className="bi bi-inbox" style={{ fontSize: "1.6rem", display: "block", marginBottom: "8px", opacity: 0.4 }}></i>
                Henüz görev eklenmedi.
              </div>
            )}
            <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
              {(() => {
                const sortedTasks = [...(ticket.tasks ?? [])].sort((a, b) => a.sortOrder - b.sortOrder);
                const canManageTasks = !isClosed && permissions.canUpdateStatus;
                const anyBusy = taskBusyId !== null || addingTask;
                return sortedTasks.map((task, index) => {
                  const busy = taskBusyId === task.id;
                  const isEditing = editingTaskId === task.id;
                  return (
                    <div key={task.id} style={{
                      display: "flex", alignItems: "center", gap: "8px",
                      padding: "8px 10px", borderRadius: "9px",
                      background: task.isCompleted ? "#f0fdf4" : "#f9fafb",
                      border: `1px solid ${task.isCompleted ? "#bbf7d0" : "#f1f5f9"}`,
                      opacity: busy ? 0.6 : 1,
                    }}>
                      <button
                        type="button"
                        onClick={() => handleToggleTask(task)}
                        disabled={!canManageTasks || anyBusy || isEditing}
                        style={{
                          background: "none", border: "none", padding: 0, lineHeight: 1,
                          cursor: canManageTasks ? "pointer" : "default", flexShrink: 0,
                        }}
                      >
                        <i
                          className={`bi ${task.isCompleted ? "bi-check-square-fill" : "bi-square"}`}
                          style={{ color: task.isCompleted ? "#16a34a" : "#9ca3af", fontSize: "1rem" }}
                        ></i>
                      </button>
                      {isEditing ? (
                        <>
                          <input
                            type="text"
                            autoFocus
                            value={editTaskTitle}
                            onChange={(e) => setEditTaskTitle(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") { e.preventDefault(); handleSaveEditTask(task); }
                              if (e.key === "Escape") { e.preventDefault(); handleCancelEditTask(); }
                            }}
                            maxLength={200}
                            style={{
                              flex: 1, minWidth: 0, padding: "5px 8px", boxSizing: "border-box",
                              border: "1.5px solid #6366f1", borderRadius: "7px",
                              fontSize: "0.85rem", outline: "none", color: "#111827",
                            }}
                          />
                          <button
                            type="button"
                            title="Kaydet"
                            onClick={() => handleSaveEditTask(task)}
                            disabled={!editTaskTitle.trim() || anyBusy}
                            style={{ background: "none", border: "none", padding: 0, lineHeight: 1, color: "#16a34a", cursor: "pointer", flexShrink: 0, fontSize: "1rem" }}
                          >
                            <i className="bi bi-check-lg"></i>
                          </button>
                          <button
                            type="button"
                            title="Vazgeç"
                            onClick={handleCancelEditTask}
                            disabled={anyBusy}
                            style={{ background: "none", border: "none", padding: 0, lineHeight: 1, color: "#9ca3af", cursor: "pointer", flexShrink: 0, fontSize: "1rem" }}
                          >
                            <i className="bi bi-x-lg"></i>
                          </button>
                        </>
                      ) : (
                        <>
                          <span style={{
                            flex: 1, minWidth: 0, fontSize: "0.85rem",
                            color: task.isCompleted ? "#166534" : "#374151",
                            textDecoration: task.isCompleted ? "line-through" : "none",
                            wordBreak: "break-word",
                          }}>
                            {task.title}
                          </span>
                          <span
                            title="Görev Ağırlığı — bu görevin ticketin tamamlanma yüzdesine katkısı"
                            style={{
                              fontSize: "0.68rem", fontWeight: 700, color: "#4f46e5",
                              background: "#eef2ff", padding: "1px 8px", borderRadius: "20px", flexShrink: 0,
                            }}>
                            {task.weight}
                          </span>
                          {canManageTasks && (
                            <button
                              type="button"
                              title="Görev adını düzenle"
                              onClick={() => handleStartEditTask(task)}
                              disabled={anyBusy}
                              style={{ background: "none", border: "none", padding: 0, lineHeight: 1, color: "#9ca3af", cursor: "pointer", flexShrink: 0, fontSize: "0.9rem" }}
                            >
                              <i className="bi bi-pencil-fill"></i>
                            </button>
                          )}
                        </>
                      )}
                      {!isEditing && canManageTasks && sortedTasks.length > 1 && (
                        <div style={{ display: "flex", flexDirection: "column", gap: "1px", flexShrink: 0 }}>
                          <button
                            type="button"
                            title="Yukarı taşı"
                            onClick={() => handleMoveTask(sortedTasks, index, -1)}
                            disabled={index === 0 || anyBusy}
                            style={{
                              background: "none", border: "none", padding: 0, lineHeight: 1,
                              color: index === 0 ? "#d1d5db" : "#9ca3af",
                              cursor: index === 0 || anyBusy ? "not-allowed" : "pointer",
                            }}
                          >
                            <i className="bi bi-caret-up-fill" style={{ fontSize: "0.7rem" }}></i>
                          </button>
                          <button
                            type="button"
                            title="Aşağı taşı"
                            onClick={() => handleMoveTask(sortedTasks, index, 1)}
                            disabled={index === sortedTasks.length - 1 || anyBusy}
                            style={{
                              background: "none", border: "none", padding: 0, lineHeight: 1,
                              color: index === sortedTasks.length - 1 ? "#d1d5db" : "#9ca3af",
                              cursor: index === sortedTasks.length - 1 || anyBusy ? "not-allowed" : "pointer",
                            }}
                          >
                            <i className="bi bi-caret-down-fill" style={{ fontSize: "0.7rem" }}></i>
                          </button>
                        </div>
                      )}
                    </div>
                  );
                });
              })()}
            </div>
          </div>

          {/* Footer */}
          <div style={{ padding: "14px 22px 18px", borderTop: "1.5px solid #f1f5f9", display: "flex", justifyContent: "flex-end", flexShrink: 0 }}>
            <button
              onClick={() => setShowTasksPopup(false)}
              style={{
                padding: "9px 22px", borderRadius: "10px", border: "none",
                background: "linear-gradient(135deg, #6366f1, #4f46e5)",
                color: "#fff", fontWeight: 700, fontSize: "0.85rem", cursor: "pointer",
              }}
            >
              Tamam
            </button>
          </div>
        </div>
      </div>
    )}

    {/* Büyük mesaj görünümü — yalnızca kapat düğmesi / Esc ile kapanır, dışarı tıklama kapatmaz */}
    {showExpanded && (
      <div style={{
        position: "fixed", inset: 0, zIndex: 1990,
        background: "rgba(15,23,42,0.55)", backdropFilter: "blur(3px)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: isMobile ? 0 : "24px 16px",
      }}>
        <div style={{
          background: "#fff",
          width: isMobile ? "100%" : "min(1500px, 100%)",
          height: isMobile ? "100%" : "min(92vh, 100%)",
          borderRadius: isMobile ? 0 : 18,
          boxShadow: "0 24px 64px rgba(0,0,0,0.25)",
          display: "flex", flexDirection: "column", overflow: "hidden",
        }}>
          <div style={{
            padding: isMobile ? "10px 12px" : "14px 20px", borderBottom: "1.5px solid #f1f5f9",
            display: "flex", alignItems: "center", gap: isMobile ? 8 : 12, flexShrink: 0,
            paddingTop: isMobile ? "max(10px, env(safe-area-inset-top))" : undefined,
          }}>
            {!isMobile && <i className="bi bi-chat-dots-fill text-primary" style={{ fontSize: "1.1rem" }}></i>}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 800, fontSize: "1rem", color: "#111827", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {ticket.title}
              </div>
              <div style={{ fontSize: "0.75rem", color: "#6b7280" }}>{messages.length} mesaj</div>
            </div>
            <div style={{ display: "inline-flex", alignItems: "center", border: "1.5px solid #e5e7eb", borderRadius: 10, overflow: "hidden", background: "#f9fafb", flexShrink: 0 }}>
              <button type="button" title="Yazıyı küçült" onClick={() => changeZoom(expandedZoom - 0.1)} disabled={expandedZoom <= 0.7}
                style={{ border: "none", background: "none", padding: isMobile ? "7px 8px" : "7px 11px", cursor: expandedZoom <= 0.7 ? "not-allowed" : "pointer", color: expandedZoom <= 0.7 ? "#cbd5e1" : "#374151", fontWeight: 700, fontSize: "0.8rem" }}>
                A<i className="bi bi-dash" style={{ fontSize: "0.75rem" }}></i>
              </button>
              <button type="button" title="Varsayılan boyut" onClick={() => changeZoom(1)}
                style={{ border: "none", borderLeft: "1.5px solid #e5e7eb", borderRight: "1.5px solid #e5e7eb", background: "#fff", padding: isMobile ? "7px 6px" : "7px 10px", cursor: "pointer", color: "#4f46e5", fontWeight: 700, fontSize: "0.78rem", minWidth: isMobile ? 44 : 52 }}>
                %{Math.round(expandedZoom * 100)}
              </button>
              <button type="button" title="Yazıyı büyüt" onClick={() => changeZoom(expandedZoom + 0.1)} disabled={expandedZoom >= 1.8}
                style={{ border: "none", background: "none", padding: isMobile ? "7px 8px" : "7px 11px", cursor: expandedZoom >= 1.8 ? "not-allowed" : "pointer", color: expandedZoom >= 1.8 ? "#cbd5e1" : "#374151", fontWeight: 700, fontSize: "1rem" }}>
                A<i className="bi bi-plus" style={{ fontSize: "0.8rem" }}></i>
              </button>
            </div>
            <button
              type="button"
              onClick={() => setShowExpanded(false)}
              title="Kapat (Esc)"
              style={{
                display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6,
                padding: isMobile ? 0 : "7px 14px", width: isMobile ? 36 : undefined, height: isMobile ? 36 : undefined,
                borderRadius: 10, flexShrink: 0,
                border: "1.5px solid #e5e7eb", background: "#f9fafb",
                color: "#374151", fontWeight: 700, fontSize: "0.85rem", cursor: "pointer",
              }}
            >
              <i className="bi bi-x-lg"></i>{!isMobile && "Kapat"}
            </button>
          </div>
          <div style={{ flex: 1, overflowY: "auto", padding: isMobile ? "12px 10px max(12px, env(safe-area-inset-bottom))" : "24px 32px", background: "#fcfcfd" }}>
            {/* zoom: avatar, balon ve yazıyı birlikte orantılı büyütür/küçültür */}
            <div style={{ zoom: expandedZoom } as React.CSSProperties}>
              {renderMessages(true)}
            </div>
            <div ref={expandedEndRef} />
          </div>
        </div>
      </div>
    )}

    {lightboxSrc && (
      <ImageLightbox src={lightboxSrc} onClose={() => setLightboxSrc(null)} />
    )}
    </>
  );
}
